
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What is a KML file?",
    answer: "KML (Keyhole Markup Language) is a file format used to display geographic data in applications like Google Earth, Google Maps, and other mapping software. It contains coordinates, shapes, and metadata for geographic features like polygons, lines, and points."
  },
  {
    question: "How do I use the KML Generator?",
    answer: "Using our KML generator is simple: click on the map to start drawing polygons by placing points, hold the spacebar to temporarily switch to drag mode for map navigation, and click 'Download KML' when you're finished drawing your areas. You can also use the address search to quickly navigate to specific locations."
  },
  {
    question: "Is this KML generator free to use?",
    answer: "Yes, our KML generator is completely free to use. There's no registration required, no watermarks on the exported files, and no limitations on the number of areas you can draw or KML files you can export."
  },
  {
    question: "Can I import the generated KML files into Google Earth?",
    answer: "Absolutely! The KML files generated by our tool are fully compatible with Google Earth, Google Maps, and most other mapping applications that support the standard KML format. Simply open the downloaded file in your preferred mapping application."
  },
  {
    question: "What can I use KML files for?",
    answer: "KML files have many practical applications including: marking property boundaries, planning hiking or travel routes, creating geographic presentations, surveying and mapping projects, sharing locations with others, and importing data into GIS software."
  },
  {
    question: "How accurate are the area calculations?",
    answer: "Our tool provides accurate area calculations using standard geographic formulas. The measurements are displayed in both square meters and square kilometers, making it suitable for both small and large area calculations."
  },
  {
    question: "Can I draw multiple areas in one KML file?",
    answer: "Yes! You can draw multiple polygons and shapes on the map, and they will all be included in a single KML file when you download it. Each area will be labeled and can be individually managed in mapping applications."
  },
  {
    question: "Do I need to install any software to use this tool?",
    answer: "No installation required! Our KML generator runs entirely in your web browser. It works on desktop computers, tablets, and mobile devices with any modern web browser."
  }
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="max-w-4xl mx-auto mt-16 mb-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-lg text-gray-600">
          Everything you need to know about our KML generator
        </p>
      </div>

      <div className="space-y-4">
        {faqData.map((item, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-900 pr-4">
                {item.question}
              </h3>
              {openItems.includes(index) ? (
                <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
              )}
            </button>
            
            {openItems.includes(index) && (
              <div className="px-6 pb-4">
                <p className="text-gray-700 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-blue-900 mb-2">
            Ready to create your KML files?
          </h3>
          <p className="text-blue-700">
            Start drawing areas on the map above and export them to KML format in seconds!
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
